import React, { useEffect, useState } from 'react';
import {
  Card,
  Flex,
  Typography,
  Grid,
  Skeleton,
  Button,
  Space,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  CrownOutlined,
  TrophyOutlined,
  UserOutlined,
  HourglassOutlined,
} from '@ant-design/icons';
import { getRequestWithParams } from '../../../api/httpRequest';
import { useLocation } from 'react-router';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface TourResult {
  max: number;
  result: number;
  status: string | null;
}

interface ResultsData {
  first: TourResult;
  second?: TourResult | null; // допускаем null
}

interface TourCardProps {
  data?: TourResult | null;
  title: string;
  footer?: React.ReactNode;
}

const TourCard: React.FC<TourCardProps> = ({ data, title, footer }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  if (!data) {
    return (
      <Card
        style={{
          height: '100%',
          borderRadius: 16,
          backgroundColor: '#f5f5f5',
          border: '1px solid #d9d9d9',
          boxShadow: 'none',
        }}
        bodyStyle={{ padding: isMobile ? 16 : 24 }}
      >
        <Flex vertical align="center" justify="center" style={{ height: '100%', minHeight: 200 }}>
          <MinusCircleOutlined style={{ fontSize: 32, color: '#bfbfbf', marginBottom: 12 }} />
          <Text type="secondary" style={{ fontSize: 16, fontWeight: 500 }}>
            {title}
          </Text>
          <Text type="secondary" style={{ fontSize: 14 }}>недоступен</Text>
        </Flex>
      </Card>
    );
  }

  // Определяем параметры статуса
  let statusIcon: React.ReactNode;
  let statusText = '';
  let statusColor = '';
  let isWinner = false;
  let isPrizewinner = false;

  if (data.status === null || data.status === 'null') {
    statusIcon = <HourglassOutlined style={{ color: '#fa8c16' }} />;
    statusText = 'Ожидание';
    statusColor = '#fa8c16';
  } else {
    switch (data.status) {
      case 'SUCCESS':
        statusIcon = <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        statusText = 'Пройден';
        statusColor = '#52c41a';
        break;
      case 'FAILED':
        statusIcon = <CloseCircleOutlined style={{ color: '#f5222d' }} />;
        statusText = 'Не пройден';
        statusColor = '#f5222d';
        break;
      case 'WINNER':
        statusIcon = <CrownOutlined style={{ color: '#faad14' }} />;
        statusText = 'Победитель';
        statusColor = '#faad14';
        isWinner = true;
        break;
      case 'PRIZEWINNER':
        statusIcon = <TrophyOutlined style={{ color: '#52c41a' }} />;
        statusText = 'Призёр';
        statusColor = '#52c41a';
        isPrizewinner = true;
        break;
      case 'PARTICIPANT':
        statusIcon = <UserOutlined style={{ color: '#8c8c8c' }} />;
        statusText = 'Участник';
        statusColor = '#8c8c8c';
        break;
      default:
        statusIcon = <MinusCircleOutlined style={{ color: '#8c8c8c' }} />;
        statusText = data.status || 'Неизвестно';
        statusColor = '#8c8c8c';
    }
  }

  const getCardStyles = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: '100%',
      borderRadius: 16,
      border: '1px solid #f0f0f0',
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
      transition: 'all 0.2s',
    };

    if (isWinner) {
      return {
        ...base,
        border: '2px solid #faad14',
        backgroundColor: '#fffbe6',
        boxShadow: '0 4px 12px rgba(250, 173, 20, 0.1)',
      };
    }

    if (isPrizewinner) {
      return {
        ...base,
        border: '1px solid #52c41a',
        backgroundColor: '#f6ffed',
        boxShadow: '0 4px 12px rgba(82, 196, 26, 0.1)',
      };
    }

    return base;
  };

  return (
    <Card style={getCardStyles()} bodyStyle={{ padding: isMobile ? 16 : 24 }}>
      <Flex vertical gap="middle">
        <Flex
          align="center"
          justify="space-between"
          style={{
            paddingBottom: 12,
            borderBottom: '1px solid #f0f0f0',
            marginBottom: 8,
          }}
        >
          <Text strong style={{ fontSize: 16 }}>
            {title}
          </Text>
          <Space size={4}>
            {statusIcon}
            <Text style={{ color: statusColor, fontWeight: 500 }}>{statusText}</Text>
          </Space>
        </Flex>

        <Flex vertical align="center" gap={4}>
          <Title
            level={1}
            style={{
              margin: 0,
              fontSize: isMobile ? 48 : 64,
              color: isWinner ? '#faad14' : isPrizewinner ? '#52c41a' : '#1677ff',
              lineHeight: 1.2,
            }}
          >
            {data.result}
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            максимальный балл: {data.max}
          </Text>
        </Flex>

        {footer && <div style={{ marginTop: 16, textAlign: 'center' }}>{footer}</div>}
      </Flex>
    </Card>
  );
};

const ResultsTab: React.FC = () => {
  const [results, setResults] = useState<ResultsData | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    setLoading(true);
    getRequestWithParams("/server/olympiad/results", { olympiadId: location.state?.olympiad?.id })
      .then(response => {
        setResults(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.log(error);
        setLoading(false);
      });
  }, [location.state?.olympiad?.id]);

  if (loading) {
    return (
      <div style={{ padding: '16px 0' }}>
        <Title level={4}>Результаты олимпиады</Title>
        <Flex gap="middle" wrap="wrap">
          <div style={{ flex: 1, minWidth: 250 }}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
          <div style={{ flex: 1, minWidth: 250 }}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        </Flex>
      </div>
    );
  }

  const firstPassed = results?.first?.status === 'SUCCESS';
  const secondData = results?.second;

  const renderSecondTour = () => {
    // Если первый тур не пройден
    if (!firstPassed) {
      return (
        <div style={{ flex: 1, minWidth: 250, maxWidth: 350 }}>
          <Card
            style={{
              height: '100%',
              borderRadius: 16,
              backgroundColor: '#fff1f0',
              border: '1px solid #ffa39e',
            }}
            bodyStyle={{ padding: 24 }}
          >
            <Flex vertical align="center" justify="center" style={{ height: '100%', minHeight: 200 }}>
              <CloseCircleOutlined style={{ fontSize: 32, color: '#f5222d', marginBottom: 12 }} />
              <Text strong style={{ fontSize: 16, color: '#f5222d' }}>
                Второй тур
              </Text>
              <Text style={{ color: '#f5222d', textAlign: 'center' }}>
                Вы не прошли первый тур
              </Text>
            </Flex>
          </Card>
        </div>
      );
    }

    // Если первый тур пройден, но данные второго отсутствуют (null или undefined)
    if (!secondData) {
      return (
        <div style={{ flex: 1, minWidth: 250, maxWidth: 350 }}>
          <Card
            style={{
              height: '100%',
              borderRadius: 16,
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
            }}
            bodyStyle={{ padding: 24 }}
          >
            <Flex vertical align="center" justify="center" style={{ height: '100%', minHeight: 200 }}>
              <HourglassOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 12 }} />
              <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                Второй тур
              </Text>
              <Text style={{ color: '#52c41a', textAlign: 'center' }}>
                Идет подсчет результатов
              </Text>
            </Flex>
          </Card>
        </div>
      );
    }

    // Если первый тур пройден и данные второго есть, проверяем статус
    const isPending = secondData.status === null || secondData.status === 'null';

    return (
      <div style={{ flex: 1, minWidth: 250, maxWidth: 350 }}>
        <TourCard
          data={secondData}
          title="Второй тур"
          footer={isPending ? <Text type="secondary">Результаты ещё не окончательные</Text> : undefined}
        />
      </div>
    );
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <Title level={4}>Результаты олимпиады</Title>
      <Flex
        gap="middle"
        wrap="wrap"
        justify="center"
        style={{ marginTop: 16 }}
      >
        <div style={{ flex: 1, minWidth: 250, maxWidth: 350 }}>
          <TourCard data={results?.first} title="Первый тур" />
        </div>

        {renderSecondTour()}
      </Flex>
    </div>
  );
};

export default ResultsTab;